import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, orderBy, getDocs, documentId } from 'firebase/firestore';
import { httpsCallable } from '../firebase/guardedWrites';
import { functions } from '../firebase/config';
import { syncFromCCB } from '../firebase/functionsService';
import '../styles/DataSync.css';
import { readOnlyProps } from '../utils/viewAs';

// Format phone number to (XXX) XXX-XXXX format
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

function DataSync() {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [lastSyncInfo, setLastSyncInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [individualId, setIndividualId] = useState('');
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);

  // Tile expansion state
  const [expandedTile, setExpandedTile] = useState(null); // 'synced' | 'new' | 'updated' | 'failed' | null
  const [tileProfiles, setTileProfiles] = useState([]);
  const [tileLoading, setTileLoading] = useState(false);

  useEffect(() => {
    loadLastSyncInfo();
  }, []);

  const loadLastSyncInfo = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const syncDocRef = doc(db, 'systemInfo', 'lastCCBSync');
      const syncDoc = await getDoc(syncDocRef);

      if (syncDoc.exists()) {
        setLastSyncInfo(syncDoc.data());
      } else {
        setLastSyncInfo(null);
      }
    } catch (error) {
      console.error('Error loading last sync info:', error);
      setLastSyncInfo(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch participant profiles by IDs from Firestore
   */
  const fetchProfilesByIds = async (profileIds) => {
    if (!profileIds || profileIds.length === 0) return [];

    const db = getFirestore();
    const profiles = [];

    // Firestore 'in' queries support max 30 items, batch if needed
    const batchSize = 30;
    for (let i = 0; i < profileIds.length; i += batchSize) {
      const batch = profileIds.slice(i, i + batchSize);
      const participantsRef = collection(db, 'participants');
      const q = query(participantsRef, where(documentId(), 'in', batch));
      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        profiles.push({ id: doc.id, ...doc.data() });
      });
    }

    // Sort by lastName, firstName
    profiles.sort((a, b) => {
      const lastCmp = (a.lastName || '').localeCompare(b.lastName || '');
      return lastCmp !== 0 ? lastCmp : (a.firstName || '').localeCompare(b.firstName || '');
    });

    return profiles;
  };

  /**
   * Fetch all synced profiles (for "Profiles Synced" tile)
   */
  const fetchAllSyncedProfiles = async () => {
    const db = getFirestore();
    const participantsRef = collection(db, 'participants');
    const q = query(
      participantsRef,
      where('groupId', 'in', ['1269', '715']),
      orderBy('lastName')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  /**
   * Handle tile click - expand/collapse and load profiles
   */
  const handleTileClick = async (tileType) => {
    // Toggle off if clicking the same tile
    if (expandedTile === tileType) {
      setExpandedTile(null);
      setTileProfiles([]);
      return;
    }

    setExpandedTile(tileType);
    setTileLoading(true);
    setTileProfiles([]);

    try {
      let profiles = [];

      switch (tileType) {
        case 'synced':
          profiles = await fetchAllSyncedProfiles();
          break;
        case 'new':
          if (lastSyncInfo?.newProfileIds?.length > 0) {
            profiles = await fetchProfilesByIds(lastSyncInfo.newProfileIds);
          }
          break;
        case 'updated':
          if (lastSyncInfo?.updatedProfileIds?.length > 0) {
            profiles = await fetchProfilesByIds(lastSyncInfo.updatedProfileIds);
          }
          break;
        case 'failed':
          if (lastSyncInfo?.failedProfileIds?.length > 0) {
            profiles = await fetchProfilesByIds(lastSyncInfo.failedProfileIds);
          }
          break;
        default:
          break;
      }

      setTileProfiles(profiles);
    } catch (error) {
      console.error('Error loading tile profiles:', error);
      setTileProfiles([]);
    } finally {
      setTileLoading(false);
    }
  };

  const handleSyncNow = async (forceResync = false) => {
    setSyncing(true);
    setSyncMessage(null);
    // Collapse any expanded tile during sync
    setExpandedTile(null);
    setTileProfiles([]);

    try {
      console.log(`Starting manual CCB sync (forceResync: ${forceResync})...`);
      // Manual sync: bypass the per-run NEW_LIMIT (10) used by the hourly
      // schedule. 100 fits inside the 540s function timeout for most groups;
      // larger imports can be completed by clicking Sync Now repeatedly.
      const result = await syncFromCCB({ groupIds: ['1269', '715'], forceResync, newLimit: 100 });
      console.log('Sync result:', result);

      // Build detailed sync message
      let messageText = `Sync complete! ${result.synced} profiles synced.`;

      if (result.skipped > 0) {
        messageText += `\n• ${result.skipped} profiles already in database`;
        messageText += `\n• Progress: ${result.total - result.skipped}/${result.total} profiles synced`;
      }

      if (result.newProfiles && result.newProfiles.length > 0) {
        messageText += `\n• ${result.newProfiles.length} new profiles added`;
      }

      if (result.updatedProfiles && result.updatedProfiles.length > 0) {
        messageText += `\n• ${result.updatedProfiles.length} profiles updated`;

        // Show examples of updated profiles
        const examples = result.updatedProfiles.slice(0, 3).map(p =>
          `  - ${p.name} (${p.changedFields.join(', ')})`
        ).join('\n');

        if (examples) {
          messageText += `:\n${examples}`;
          if (result.updatedProfiles.length > 3) {
            messageText += `\n  ... and ${result.updatedProfiles.length - 3} more`;
          }
        }
      }

      if (result.unchangedProfiles && result.unchangedProfiles.length > 0) {
        messageText += `\n• ${result.unchangedProfiles.length} profiles unchanged`;
      }

      if (result.failed > 0) {
        messageText += `\n• ${result.failed} failed`;
      }

      setSyncMessage({
        type: result.failed > 0 ? 'warning' : 'success',
        text: messageText,
        details: result
      });

      // Reload last sync info
      await loadLastSyncInfo();

      // Clear message after 15 seconds
      setTimeout(() => setSyncMessage(null), 15000);
    } catch (error) {
      console.error('Sync error:', error);
      setSyncMessage({
        type: 'error',
        text: `Failed to sync from CCB: ${error.message}`
      });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTileLabel = (tileType) => {
    switch (tileType) {
      case 'synced': return 'Profiles Synced';
      case 'new': return 'New Profiles';
      case 'updated': return 'Updated Profiles';
      case 'failed': return 'Failed';
      default: return '';
    }
  };

  return (
    <div className="data-sync-container">
      {/* Header */}
      <div className="data-sync-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1>Data Synchronization</h1>
      </div>

      {/* Sync Status Card */}
      <div className="sync-status-card">
        <h2>CCB Sync Status</h2>

        {loading ? (
          <div className="loading-message">Loading sync status...</div>
        ) : (
          <>
            <div className="sync-info-grid">
              <div className="sync-info-item">
                <span className="sync-info-label">Last Sync:</span>
                <span className="sync-info-value">
                  {lastSyncInfo?.timestamp ? formatDate(lastSyncInfo.timestamp) : 'Never'}
                </span>
              </div>

              {lastSyncInfo && (
                <>
                  <div
                    className={`sync-info-item sync-tile-clickable ${expandedTile === 'synced' ? 'sync-tile-active' : ''}`}
                    onClick={() => handleTileClick('synced')}
                    title="Click to view synced profiles"
                  >
                    <span className="sync-info-label">Profiles Synced:</span>
                    <span className="sync-info-value">{lastSyncInfo.synced || 0}</span>
                    <span className="sync-tile-hint">Click to view</span>
                  </div>

                  <div
                    className={`sync-info-item sync-tile-clickable ${expandedTile === 'new' ? 'sync-tile-active' : ''} ${(lastSyncInfo.newProfiles || 0) === 0 ? 'sync-tile-empty' : ''}`}
                    onClick={() => (lastSyncInfo.newProfiles || 0) > 0 ? handleTileClick('new') : null}
                    title={`${(lastSyncInfo.newProfiles || 0) > 0 ? 'Click to view new profiles' : 'No new profiles from last sync'}`}
                  >
                    <span className="sync-info-label">New Profiles:</span>
                    <span className="sync-info-value">{lastSyncInfo.newProfiles || 0}</span>
                    {(lastSyncInfo.newProfiles || 0) > 0 && (
                      <span className="sync-tile-hint">Click to view</span>
                    )}
                  </div>

                  <div
                    className={`sync-info-item sync-tile-clickable ${expandedTile === 'updated' ? 'sync-tile-active' : ''} ${(lastSyncInfo.updatedProfiles || 0) === 0 ? 'sync-tile-empty' : ''}`}
                    onClick={() => (lastSyncInfo.updatedProfiles || 0) > 0 ? handleTileClick('updated') : null}
                    title={`${(lastSyncInfo.updatedProfiles || 0) > 0 ? 'Click to view updated profiles' : 'No updated profiles from last sync'}`}
                  >
                    <span className="sync-info-label">Updated Profiles:</span>
                    <span className="sync-info-value">{lastSyncInfo.updatedProfiles || 0}</span>
                    {(lastSyncInfo.updatedProfiles || 0) > 0 && (
                      <span className="sync-tile-hint">Click to view</span>
                    )}
                  </div>

                  <div
                    className={`sync-info-item sync-tile-clickable ${expandedTile === 'failed' ? 'sync-tile-active' : ''} ${(lastSyncInfo.failed || 0) === 0 ? 'sync-tile-empty' : ''}`}
                    onClick={() => (lastSyncInfo.failed || 0) > 0 ? handleTileClick('failed') : null}
                    title={`${(lastSyncInfo.failed || 0) > 0 ? 'Click to view failed profiles' : 'No failures from last sync'}`}
                  >
                    <span className="sync-info-label">Failed:</span>
                    <span className="sync-info-value" style={{ color: lastSyncInfo.failed > 0 ? '#ef4444' : 'inherit' }}>
                      {lastSyncInfo.failed || 0}
                    </span>
                    {(lastSyncInfo.failed || 0) > 0 && (
                      <span className="sync-tile-hint">Click to view</span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Expanded Profile List */}
            {expandedTile && (
              <div className="sync-profile-list-container">
                <div className="sync-profile-list-header">
                  <h3>{getTileLabel(expandedTile)} ({tileLoading ? '...' : tileProfiles.length})</h3>
                  <button
                    className="sync-profile-list-close"
                    onClick={() => { setExpandedTile(null); setTileProfiles([]); }}
                  >
                    Close
                  </button>
                </div>

                {tileLoading ? (
                  <div className="sync-profile-list-loading">
                    <div className="spinner-inline"></div>
                    <span>Loading profiles...</span>
                  </div>
                ) : tileProfiles.length === 0 ? (
                  <div className="sync-profile-list-empty">
                    No profile details available for this category.
                  </div>
                ) : (
                  <div className="sync-profile-list">
                    {tileProfiles.map((profile) => (
                      <div key={profile.id} className="sync-profile-card">
                        <div className="sync-profile-avatar">
                          {profile.image ? (
                            <img src={profile.image} alt={`${profile.firstName} ${profile.lastName}`} />
                          ) : (
                            <div className="sync-profile-initials">
                              {(profile.firstName || '?').charAt(0)}{(profile.lastName || '?').charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="sync-profile-info">
                          <div className="sync-profile-name">
                            {profile.firstName} {profile.lastName}
                          </div>
                          <div className="sync-profile-contact">
                            {(profile.mobilePhone || profile.phone || profile.homePhone) && (
                              <span className="sync-profile-detail">
                                📱 {formatPhoneNumber(profile.mobilePhone || profile.phone || profile.homePhone)}
                              </span>
                            )}
                            {profile.email && (
                              <span className="sync-profile-detail">
                                ✉️ {profile.email}
                              </span>
                            )}
                            {!profile.mobilePhone && !profile.phone && !profile.homePhone && !profile.email && (
                              <span className="sync-profile-detail sync-profile-no-contact">
                                No contact info
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="sync-profile-id">
                          {profile.id}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="scheduled-sync-info">
              <p>
                <strong>Scheduled Sync:</strong> Automatic sync runs daily at 3:00 AM EST
              </p>
              <p className="sync-description">
                The scheduled sync pulls profile updates from CCB (Church Community Builder)
                to keep the app's participant database synchronized. Only changed profiles are updated.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Sync Message */}
      {syncMessage && (
        <div className={`sync-message ${syncMessage.type}`}>
          <pre>{syncMessage.text}</pre>
        </div>
      )}

      {/* Manual Sync Controls */}
      <div className="sync-controls-card">
        <h2>Manual Sync</h2>
        <p className="sync-controls-description">
          Manually trigger a sync from CCB to Firestore. This will update up to 10 profiles
          that haven't been synced recently. Click "Sync Now" multiple times to sync additional batches.
        </p>

        <div className="sync-buttons">
          <button
            className="btn btn-primary btn-sync-now"
            onClick={() => handleSyncNow(false)}
            disabled={syncing || loading || ro.disabled}
            title={ro.title}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>

          <button
            className="btn btn-secondary btn-force-resync"
            onClick={() => handleSyncNow(true)}
            disabled={syncing || loading || ro.disabled}
            title={ro.title || 'Re-sync existing profiles to update all fields from CCB'}
          >
            {syncing ? 'Syncing...' : 'Force Resync (10)'}
          </button>
        </div>

        <p className="sync-controls-note" style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
          <strong>Force Resync</strong> will re-download and update profiles with missing gender from CCB.
        </p>

        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
            Sync Specific CCB ID:
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={individualId}
              onChange={(e) => setIndividualId(e.target.value)}
              placeholder="e.g., 64319 or 112263,110081"
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={async () => {
                if (!individualId.trim()) return;
                setSyncing(true);
                setSyncMessage(null);
                try {
                  const ids = individualId.split(',').map(id => id.trim()).filter(id => id);
                  const result = await syncFromCCB({ individualIds: ids });
                  const synced = result?.synced ?? 0;
                  const failed = result?.failed ?? 0;
                  const idLabel = ids.length === 1 ? `ID ${ids[0]}` : `${ids.length} IDs`;
                  if (failed === 0) {
                    setSyncMessage({
                      type: 'success',
                      text: `Synced ${synced} ${synced === 1 ? 'profile' : 'profiles'} from CCB (${idLabel}).`
                    });
                  } else {
                    const errDetail = (result?.errors || [])
                      .map(e => `${e.individualId}: ${e.error}`)
                      .join('; ');
                    setSyncMessage({
                      type: failed === ids.length ? 'error' : 'warning',
                      text: `Synced ${synced} of ${ids.length} (${idLabel}). ${failed} failed${errDetail ? ` — ${errDetail}` : '.'}`
                    });
                  }
                } catch (error) {
                  setSyncMessage({ type: 'error', text: `Error: ${error.message}` });
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing || !individualId.trim() || ro.disabled}
              title={ro.title}
            >
              Sync ID
            </button>
          </div>
        </div>

        {syncing && (
          <div className="sync-progress-message">
            <div className="spinner-inline"></div>
            <span>Syncing profiles from CCB...</span>
          </div>
        )}

        {/* Volunteer checkout backfill — one-shot retroactive close-out */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Volunteer Checkout Backfill</div>
          <div style={{ marginBottom: '12px', fontSize: '13px', color: '#6b7280' }}>
            One-time: stamps `checkOutTime` on every historical volunteer check-in that was never closed out.
            Each record gets `checkOutTime` set to 11:59:59 PM of the day they checked in. Idempotent — safe to re-run.
          </div>
          <button
            className="btn btn-primary"
            onClick={async () => {
              setSyncing(true);
              setSyncMessage(null);
              try {
                const callable = httpsCallable(functions, 'backfillVolunteerCheckOuts');
                const result = await callable({});
                const r = result.data || {};
                const parts = [`closed out ${r.checkedOut} of ${r.total}`];
                if (r.reopened) parts.push(`reopened ${r.reopened} active today`);
                if (r.skipped) parts.push(`skipped ${r.skipped}`);
                if (r.missingCheckInTime) parts.push(`${r.missingCheckInTime} missing checkInTime`);
                setSyncMessage({
                  type: 'success',
                  text: `Backfill complete — ${parts.join(', ')}.`
                });
              } catch (error) {
                setSyncMessage({ type: 'error', text: `Backfill error: ${error.message}` });
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing || ro.disabled}
            title={ro.title}
          >
            Run Volunteer Checkout Backfill
          </button>
        </div>
      </div>

      {/* Gender Comparison */}
      <div className="sync-controls-card">
        <h2>Gender Data Comparison</h2>
        <p className="sync-controls-description">
          Compare gender data between CCB and this app to find mismatches.
        </p>
        <button
          className="btn btn-primary"
          onClick={async () => {
            setComparing(true);
            setComparisonResult(null);
            try {
              const compareGenderData = httpsCallable(functions, 'compareGenderData', { timeout: 300000 });
              const result = await compareGenderData();
              setComparisonResult(result.data);
            } catch (error) {
              setComparisonResult({ error: error.message });
            } finally {
              setComparing(false);
            }
          }}
          disabled={comparing}
        >
          {comparing ? 'Comparing...' : 'Compare Gender Data'}
        </button>

        {comparing && (
          <div className="sync-progress-message">
            <div className="spinner-inline"></div>
            <span>Fetching all profiles from CCB (this may take a few minutes)...</span>
          </div>
        )}

        {comparisonResult && !comparisonResult.error && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #d1d5db' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Results:</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>CCB (Source of Truth):</strong>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                  <li>Total profiles: {comparisonResult.ccb.total}</li>
                  <li style={{ color: '#16a34a' }}>With gender: {comparisonResult.ccb.withGender}</li>
                  <li>Without gender: {comparisonResult.ccb.withoutGender}</li>
                  {comparisonResult.ccb.errors > 0 && (
                    <li style={{ color: '#dc2626' }}>API errors (rate limited): {comparisonResult.ccb.errors}</li>
                  )}
                </ul>
              </div>
              <div>
                <strong>Firestore (App):</strong>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                  <li>Total profiles: {comparisonResult.firestore.total}</li>
                  <li style={{ color: '#16a34a' }}>Matching CCB gender: {comparisonResult.firestore.withValidGender}</li>
                  <li style={{ color: comparisonResult.firestore.withInvalidGender > 0 ? '#dc2626' : 'inherit' }}>
                    Invalid format: {comparisonResult.firestore.withInvalidGender}
                    {comparisonResult.invalidFormat?.length > 0 && (
                      <ul style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}>
                        {comparisonResult.invalidFormat.map((p, i) => (
                          <li key={i}>{p.firstName} {p.lastName} (ccb_{p.id}): "{p.gender}"</li>
                        ))}
                      </ul>
                    )}
                  </li>
                  <li>No gender: {comparisonResult.firestore.withoutGender}</li>
                </ul>
              </div>
            </div>

            {comparisonResult.mismatches.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#fef2f2', borderRadius: '4px' }}>
                <strong style={{ color: '#dc2626' }}>
                  CCB has gender but App doesn't match ({comparisonResult.mismatches.length}):
                </strong>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', maxHeight: '200px', overflowY: 'auto', fontSize: '0.9rem' }}>
                  {comparisonResult.mismatches.map((m, i) => (
                    <li key={i}>ccb_{m.id}: CCB="{m.ccbGender}" vs App="{m.firestoreGender}"</li>
                  ))}
                </ul>
              </div>
            )}

            {comparisonResult.firestoreHasButCCBDoesnt?.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#fef9c3', borderRadius: '4px' }}>
                <strong style={{ color: '#a16207' }}>
                  App has gender but CCB doesn't ({comparisonResult.firestoreHasButCCBDoesnt.length}):
                </strong>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', maxHeight: '200px', overflowY: 'auto', fontSize: '0.9rem' }}>
                  {comparisonResult.firestoreHasButCCBDoesnt.map((m, i) => (
                    <li key={i}>ccb_{m.id}: App="{m.firestoreGender}" but CCB has none</li>
                  ))}
                </ul>
              </div>
            )}

            {comparisonResult.mismatches.length === 0 && (!comparisonResult.firestoreHasButCCBDoesnt || comparisonResult.firestoreHasButCCBDoesnt.length === 0) && (
              <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#f0fdf4', borderRadius: '4px', color: '#16a34a' }}>
                <strong>All CCB gender data is correctly synced to the app!</strong>
              </div>
            )}
          </div>
        )}

        {comparisonResult?.error && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef2f2', borderRadius: '8px', color: '#dc2626' }}>
            Error: {comparisonResult.error}
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="sync-info-card">
        <h2>How Sync Works</h2>
        <div className="sync-explanation">
          <div className="sync-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>CCB is the Source of Truth</h3>
              <p>All profile data originates from CCB (Church Community Builder)</p>
            </div>
          </div>

          <div className="sync-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Server-Side Sync Only</h3>
              <p>Cloud Functions sync profiles from CCB to Firestore. The app never talks to CCB directly.</p>
            </div>
          </div>

          <div className="sync-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Real-Time Updates</h3>
              <p>The app uses Firestore real-time listeners to instantly reflect changes in the participants collection</p>
            </div>
          </div>

          <div className="sync-step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Incremental Sync</h3>
              <p>Only profiles that don't exist or need updating are synced, reducing API calls and processing time</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataSync;
